import React, { useEffect, useState } from "react";
import { styled } from "@mui/material/styles";
import { connect } from "react-redux";

import { get, isNil } from "lodash";

import HelpIcon from "@mui/icons-material/HelpOutline";
import { Button } from "@mui/material";

import { WILDCARD_ORG_ID } from "lib/api/API";

import { isOrganizationChildOfNFR } from "utils";

import { getUserOrgRoles } from "utils/sidebarUtils";

const PREFIX = "Zendesk";
const classes = {
  button: `${PREFIX}-button`,
  openButton: `${PREFIX}-openButton`,
  closedButton: `${PREFIX}-closedButton`,
  openIcon: `${PREFIX}-openIcon`,
  helpContainer: `${PREFIX}-helpContainer`,
  openHelpContainer: `${PREFIX}-openHelpContainer`,
  closedHelpContainer: `${PREFIX}-closedHelpContainer`,
  openPlanText: `${PREFIX}-openPlanText`,
  closedPlanText: `${PREFIX}-closedPlanText`,
  xdrUpsellContainer: `${PREFIX}-xdrUpsellContainer`,
  tryXdrButton: `${PREFIX}-tryXdrButton`,
};

const Root = styled("div")(({ theme }) => ({
  [`& .${classes.button}`]: {
    height: 45,
    color: "white",
    display: "flex",
    fontSize: "1rem",
    fontWeight: "700",
    alignItems: "center",
    borderRadius: "999rem",
    backgroundColor: "#3E7EF8",
    "&:hover": {
      color: "white",
      textDecoration: "none",
    },
  },
  [`& .${classes.openButton}`]: {
    width: "fit-content",
    padding: "1rem",
    justifyContent: "space-around",
  },
  [`& .${classes.closedButton}`]: {
    width: 45,
    justifyContent: "center",
  },
  [`& .${classes.openIcon}`]: {
    marginRight: 5,
  },
  [`& .${classes.helpContainer}`]: {
    position: "fixed",
    bottom: 0,
    backgroundColor: theme.palette.background.paper,
    left: 0,
    zIndex: 22000,
    transition: theme.transitions.create("width", {
      easing: "cubic-bezier(0.4, 0, 0.6, 1)",
      duration: 225,
    }),
  },
  [`& .${classes.openHelpContainer}`]: {
    padding: 20,
    whiteSpace: "normal",
    width: 219,
    borderTop: `1px solid ${theme.palette.divider}`,
  },
  [`& .${classes.closedHelpContainer}`]: {
    padding: 10,
    width: 63,
  },
  [`& .${classes.openPlanText}`]: {
    opacity: 1,
    transition: theme.transitions.create("opacity", {
      easing: "cubic-bezier(0.4, 0, 0.6, 1)",
      duration: 225,
      delay: 100,
    }),
  },
  [`& .${classes.closedPlanText}`]: {
    opacity: 0,
    transition: theme.transitions.create("opacity", {
      easing: "cubic-bezier(0.4, 0, 0.6, 1)",
      duration: 175,
    }),
  },
  [`& .${classes.xdrUpsellContainer}`]: {
    fontSize: 12,
    display: "flex",
    borderRadius: 10,
    flexDirection: "column",
    backgroundColor: "#76c89c",
    border: "1px solid #5FAF78",
    padding: theme.shape.padding,
    marginBottom: theme.shape.padding,
    color: "#3B3B3B",
  },
  [`& .${classes.tryXdrButton}`]: {
    color: "white",
    backgroundColor: "#3B3B3B",
    "&:hover": {
      backgroundColor: "#3B3B3B",
    },
  },
}));

const Zendesk = (props) => {
  const { toplevel } = props;

  let helpDeskUrl;
  let helpButtonText = "Help";

  if (toplevel === "mspportal") {
    helpDeskUrl = "https://blumira.help/partners";
  } else {
    helpDeskUrl = "https://blumira.help/help";
  }

  const [orgHasRequestedXdrTrialValue, setOrgHasRequestedXdrTrialValue] =
    useState();

  const setLocalStorageAndStateForTrial = () => {
    const hasOrgRequestedXdrTrialValue = localStorage.getItem(
      `hasOrgRequestedXdrTrial-${props.currentOrgId}`
    );

    const newBoolValue = hasOrgRequestedXdrTrialValue === "true";
    setOrgHasRequestedXdrTrialValue(newBoolValue);
  };

  useEffect(() => {
    // for changes in app
    const onStorageChange = (e) => {
      setLocalStorageAndStateForTrial();
    };

    // for initial render
    setLocalStorageAndStateForTrial();

    window.addEventListener("storage", onStorageChange);

    return () => {
      window.removeEventListener("storage", onStorageChange);
    };
  }, []);

  useEffect(() => {
    setLocalStorageAndStateForTrial();
  }, [props.currentOrgId]);

  const renderPlanText = () => {
    if (props.isAdministeringOrg) {
      return "";
    } else if (props.licenseLabel.length) {
      return `${props.licenseLabel} edition`;
    } else {
      return "";
    }
  };

  const handleViewBilling = () => {
    props.goToBillingView(props.currentOrgId);
  };

  return (
    <Root>
      {props.open ? (
        <div
          className={`${classes.helpContainer} ${classes.openHelpContainer}`}
        >
          <p className={classes.openPlanText}>{renderPlanText()}</p>
          {props.shouldDisplayUpsell && !orgHasRequestedXdrTrialValue && (
            <div className={classes.xdrUpsellContainer}>
              <p>
                Meet your compliance regulations with the full feature{" "}
                <strong>XDR PLATFORM</strong>
              </p>
              <Button
                onClick={handleViewBilling}
                className={classes.tryXdrButton}
              >
                Try XDR for Free
              </Button>
            </div>
          )}
          <a
            className={`${classes.button} ${classes.openButton}`}
            href={helpDeskUrl}
            target={"_blank"}
            rel={"noopener noreferrer"}
          >
            <HelpIcon className={classes.openIcon} />
            {helpButtonText}
          </a>
        </div>
      ) : (
        <div
          className={`${classes.helpContainer} ${classes.closedHelpContainer}`}
        >
          <p className={classes.closedPlanText}>{renderPlanText()}</p>
          <a
            className={`${classes.button} ${classes.closedButton}`}
            href={helpDeskUrl}
            target={"_blank"}
            rel={"noopener noreferrer"}
          >
            <HelpIcon />
          </a>
        </div>
      )}
    </Root>
  );
};

const mapDispatchToProps = (dispatch) => ({
  goToBillingView: (orgId) => {
    const payload = {
      orgId,
      toplevel: "settings",
      secondlevel: "billing",
    };

    dispatch({
      type: "PAGE",
      payload,
    });
  },
});

const mapStateToProps = (state) => {
  if (!state?.session?.settings?.user) return {};

  let currentOrg = {};

  const toplevel = state.location.payload.toplevel;
  const isAdministeringOrg =
    !!state.page?.payload?.orgId &&
    WILDCARD_ORG_ID === state.page.payload.orgId;

  const currentOrgId = get(state, ["page", "payload", "orgId"], null);
  const currentUserOrgs = get(state, ["session", "settings", "userOrgs"], []);

  // if we have a current org id, find org data
  if (currentOrgId)
    currentOrg = currentUserOrgs.find(({ id }) => id === currentOrgId);

  const currentOrgLicenseVal = get(currentOrg, ["config", "license"], "FREE");

  const isOrgAChildOrg = isOrganizationChildOfNFR(currentOrg);

  const isOrgAParentOrg =
    currentOrg && currentOrg?.market === 20 && isNil(currentOrg.parentId);

  const userOrgRoles = getUserOrgRoles({
    user: state.session.settings.user,
    orgId: currentOrgId,
  });

  // only admins and managers have access to billing
  const isUserAdminOrManager = ["administrator", "manager"].some((role) =>
    userOrgRoles.includes(role)
  );

  const shouldDisplayBasedOnRole =
    state.session.settings.user.superadmin || isUserAdminOrManager;

  // exclude MSPs and non-admins/managers
  const shouldDisplayUpsell =
    !isAdministeringOrg &&
    shouldDisplayBasedOnRole &&
    !isOrgAChildOrg &&
    !isOrgAParentOrg &&
    currentOrgLicenseVal === "FREE";

  return {
    toplevel: toplevel,
    currentOrgId: currentOrgId,
    isAdministeringOrg: isAdministeringOrg,
    shouldDisplayUpsell: shouldDisplayUpsell,
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(Zendesk);
